import bellSvg from './bell.svg';
import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { track } from '@/shared/utils/track';
import { captureException } from '@/modules/capture-exception';
import { useStateReducer } from '@/modules/use-state-reducer';
import SpinnerThreeDots from '@/shared/ui/SpinnerThreeDots';
import Modal from '@/shared/ui/Modal';
import DropdownContent from './DropdownContent';
import BellIcon from './BellIcon';
import { fetchApi } from '@/modules/sanr-api';
import { useProfile } from '@/routes/Providers/ProfileProvider';
import { useIsMobile } from '@/modules/use-window-width';

const TAKE = 5;

const types = [
  'user_opened_signal',
  'user_closed_signal',
  'user_auto_closed_signal',
  'signal_take_profit_triggered',
  'signal_stop_loss_triggered',
].join(',');

const NOTIFICATIONS_KEY = ['notifications'] as const;

export default function Notifications() {
  const { profile } = useProfile();
  // The account identity, NOT the profile object: updateProfile() commits a
  // brand-new object for the same account (focus refetch, settings save, tx
  // flow) and none of those may reset the loaded list — only a real account
  // change does.
  const profileId = profile?.id;
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const uiInitial: {
    isDesktopDropdownShown: boolean;
    isMobileDropdownShown: boolean;
  } = {
    isDesktopDropdownShown: false,
    isMobileDropdownShown: false,
  };
  const [{ isDesktopDropdownShown, isMobileDropdownShown }, ss] =
    useStateReducer(uiInitial);

  // take is stored with the account it was grown for and read back only for
  // that account — pagination resets on an account switch without an
  // effect-driven reset (which would fire a wasted first fetch at the old
  // take).
  const [takeState, setTakeState] = useState({ id: profileId, take: TAKE });
  const take = takeState.id === profileId ? takeState.take : TAKE;

  const {
    data: notifications,
    error,
    isFetching,
    isPlaceholderData,
  } = useQuery({
    // take is part of the key, so a slow smaller-take poll response settles
    // into its own cache entry and can never truncate a bigger rendered page.
    queryKey: [...NOTIFICATIONS_KEY, profileId, take],
    enabled: profileId !== undefined,
    queryFn: ({ signal }) =>
      fetchApi('GET_NOTIFICATIONS', {
        signal,
        queryParams: { types, sort: 'date:desc', take: String(take) },
      }),
    // Keep the loaded page on screen while a bigger take loads — but never
    // inherit another account's rows across an account switch.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[1] === profileId ? prev : undefined,
    // 10s freshness poll; compiled out under VITE_IS_LOCALHOST (which vitest
    // defines).
    refetchInterval: import.meta.env.VITE_IS_LOCALHOST ? false : 10_001,
  });
  const isMoreLoading = isFetching && isPlaceholderData;

  useEffect(() => {
    if (error) {
      captureException(error);
    }
  }, [error]);

  const dropdownContentWrRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line no-restricted-syntax -- effect dependency in the bottom-scroll interval effect below
  const loadMore = useCallback(() => {
    if (!notifications) {
      return;
    }
    const nextTake = notifications.data.length + TAKE;
    // Bail out while the bigger page is in flight (length unchanged → same
    // take), so the 300ms bottom-scroll interval doesn't re-render or
    // re-request every tick.
    setTakeState((prev) =>
      prev.id === profileId && prev.take === nextTake ?
        prev
      : { id: profileId, take: nextTake },
    );
  }, [notifications, profileId]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const markAllAsRead = async () => {
    await fetchApi('PUT_NOTIFICATIONS_READ');
    await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  };

  // One row's read mark (the list's viewport observer drives it) — the
  // write and the refetch stay here, the notifications invalidation root.
  // eslint-disable-next-line no-restricted-syntax -- identity keys DropdownContent's viewport-observer effect
  const markRead = useCallback(
    async (id: string) => {
      await fetchApi('PUT_NOTIFICATION', {
        signal: undefined,
        urlParams: [id],
        body: {},
      });
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
    [queryClient],
  );

  useEffect(() => {
    if (!notifications || notifications.data.length === 0) {
      return;
    }
    if (isDesktopDropdownShown || isMobileDropdownShown) {
      const intervalId = setInterval(() => {
        if (notifications.data.length === notifications.total) {
          return;
        }
        if (
          dropdownRef.current &&
          (isDesktopDropdownShown ?
            dropdownRef.current.scrollTop + dropdownRef.current.clientHeight >
            dropdownRef.current.scrollHeight - 48
          : dropdownRef.current.scrollTop + window.innerHeight >
            dropdownRef.current.scrollHeight - 48)
        ) {
          loadMore();
        }
      }, 300);
      return () => clearInterval(intervalId);
    }
    return undefined;
  }, [isDesktopDropdownShown, isMobileDropdownShown, loadMore, notifications]);

  const buttonRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isDesktopDropdownShown) {
      return undefined;
    }
    const onPointerDown = ({ target }: PointerEvent) => {
      if (
        target instanceof Node &&
        (dropdownContentWrRef.current?.contains(target) === true ||
          buttonRef.current?.contains(target) === true)
      ) {
        return;
      }
      ss({ isDesktopDropdownShown: false });
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isDesktopDropdownShown, ss]);

  const closeNotifications = () => {
    if (isDesktopDropdownShown) {
      ss({ isDesktopDropdownShown: false });
    }
    if (isMobileDropdownShown) {
      ss({ isMobileDropdownShown: false });
    }
  };

  const dropdownContentEl = (
    <DropdownContent
      closeNotifications={closeNotifications}
      dropdownContentWrRef={dropdownContentWrRef}
      dropdownRef={dropdownRef}
      isMoreLoading={isMoreLoading}
      markAllAsRead={markAllAsRead}
      markRead={markRead}
      notifications={notifications ?? null}
    />
  );

  const hasUnread =
    !!profile && !!notifications && notifications.unreadTotal > 0;

  const toggle = () => {
    if (!notifications) return;
    if (!isMobile) {
      if (!isDesktopDropdownShown) track('notifications_opened');
      ss({ isDesktopDropdownShown: !isDesktopDropdownShown });
    } else {
      if (!isMobileDropdownShown) track('notifications_opened');
      ss({ isMobileDropdownShown: !isMobileDropdownShown });
    }
  };

  const spinnerEl = !notifications && (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-xs py-2.5">
      <SpinnerThreeDots color="#ffffff" size={3} />
    </div>
  );

  return (
    <>
      {isMobileDropdownShown && (
        <Modal closeModal={() => ss({ isMobileDropdownShown: false })}>
          {dropdownContentEl}
        </Modal>
      )}
      {!isMobile ?
        <div className="relative flex h-3xl w-3xl flex-col items-center rounded-xs">
          <div className="relative" ref={buttonRef}>
            <button
              type="button"
              className="relative btn-reset flex h-3xl w-3xl items-center justify-center rounded-full"
              style={
                notifications ?
                  {
                    mask: `url(${JSON.stringify(bellSvg)}) no-repeat center / 20px 20px`,
                    WebkitMask: `url(${JSON.stringify(bellSvg)}) no-repeat center / 20px 20px`,
                    background: 'var(--gray-text)',
                  }
                : undefined
              }
              onClick={toggle}
            >
              {spinnerEl}
            </button>
            {hasUnread && (
              <div
                className="absolute rounded-full bg-red-default"
                style={{
                  right: '5px',
                  top: '7px',
                  width: '8px',
                  height: '8px',
                }}
              />
            )}
            {isDesktopDropdownShown && dropdownContentEl}
          </div>
        </div>
      : <button
          type="button"
          className="relative btn-reset flex size-8 items-center justify-center rounded-xs text-gray-text"
          aria-label="Notifications"
          onClick={toggle}
        >
          {notifications ?
            <BellIcon unread={hasUnread} />
          : spinnerEl}
        </button>
      }
    </>
  );
}
